import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import DataTable from '@/components/UI/DataTable';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import type { ColumnDef, SortingState } from '@/components/UI/dataTableConfig';
import FormInput from '@/components/UI/FormInput';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc, trpcClient } from '@/api/trpc.ts';
import { keepPreviousData, useMutation } from '@tanstack/react-query';
import { useAppQuery } from '@/hooks/useAppQuery';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { Button, ConfirmationDialog, StatusBadge, TradingStrategySelect } from '@/components/UI';
import { FaCopy, FaEye, FaPlay, FaRedo, FaStop, FaTrash } from 'react-icons/fa';
import { showToast } from '@/components/Layout/Toast';
import dayjs from 'dayjs';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';
import { getCatalogAuditPresentation } from './catalogAuditPresentation';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type ExperimentListItem = RouterOutputs['experiment']['list']['experiments'][number];
const PAGE_SIZE = 20;
const SORT_COLUMNS = ['id', 'name', 'strategyName', 'executionStatus', 'createdAt'] as const;

const Experiments: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [error, setError] = useState<string | null>(null);
    const [selectedExperiment, setSelectedExperiment] = useState<ExperimentListItem | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showRetryDialog, setShowRetryDialog] = useState(false);
    const [recentlyStartedExperimentIds, setRecentlyStartedExperimentIds] = useState<Set<number>>(new Set());
    const [searchText, setSearchText] = useState('');
    const [query, setQuery] = useState('');
    const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
    const pageNumber = Number(searchParams.get('page') ?? 1);
    const pageIndex = Number.isSafeInteger(pageNumber) && pageNumber > 0 ? pageNumber - 1 : 0;
    const setPageIndex = useCallback(
        (nextPageIndex: number) => {
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    if (nextPageIndex > 0) next.set('page', String(nextPageIndex + 1));
                    else next.delete('page');
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );
    useEffect(() => {
        if (searchText.trim() === query) return;
        const timeout = setTimeout(() => {
            setQuery(searchText.trim());
            setPageIndex(0);
        }, 300);
        return () => clearTimeout(timeout);
    }, [searchText, query, setPageIndex]);
    const sortBy = SORT_COLUMNS.find((column) => column === sorting[0]?.id) ?? 'createdAt';

    const selectedStrategyId = useMemo(() => {
        const strategyIdFromQuery = Number(searchParams.get('strategyId'));
        return !Number.isNaN(strategyIdFromQuery) && strategyIdFromQuery > 0 ? strategyIdFromQuery : null;
    }, [searchParams]);

    const setSelectedStrategyId = useCallback(
        (strategyId: number | null) => {
            const next = new URLSearchParams(searchParams);
            if (strategyId) next.set('strategyId', String(strategyId));
            else next.delete('strategyId');
            next.delete('page');
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    // If we were linked here with `state: { strategyId }`, promote it into the URL for deep links.
    useEffect(() => {
        if (selectedStrategyId) return;

        const strategyIdFromStateRaw = (location.state as { strategyId?: unknown } | null)?.strategyId;
        const strategyIdFromState = typeof strategyIdFromStateRaw === 'number' ? strategyIdFromStateRaw : Number(strategyIdFromStateRaw);

        if (!Number.isNaN(strategyIdFromState) && strategyIdFromState > 0) {
            setSelectedStrategyId(strategyIdFromState);
        }
    }, [location.state, selectedStrategyId, setSelectedStrategyId]);

    const preservedStrategyQuery = useMemo(() => (selectedStrategyId ? `?strategyId=${selectedStrategyId}` : ''), [selectedStrategyId]);

    const handleCreateExperiment = useCallback(() => {
        void navigate(`/user/experiment/new${preservedStrategyQuery}`);
    }, [navigate, preservedStrategyQuery]);

    const handleCompareExperiments = useCallback(() => {
        void navigate(`/user/experimentCompare${preservedStrategyQuery}`);
    }, [navigate, preservedStrategyQuery]);

    // Discovery status badge component
    const DiscoveryBadge: React.FC<{ experiment: ExperimentListItem }> = ({ experiment }) => {
        const { discoveryStatus } = experiment;
        const { pairCounts } = experiment.lifecycle;
        const catalogAuditPresentation = getCatalogAuditPresentation(experiment.catalogAudit);

        if (discoveryStatus === 'disabled') {
            return <span className="text-zinc-500 text-xs">Disabled</span>;
        }

        if (discoveryStatus === 'not_applicable') {
            return <span className="text-zinc-500 text-xs">Not applicable</span>;
        }

        if (discoveryStatus === 'not_started') {
            return <span className="text-zinc-500 text-xs">Not started</span>;
        }

        if (discoveryStatus === 'preparing_objective') {
            return (
                <span aria-label="Preparing realized objective (materializing outcomes before discovery)" role="status">
                    <StatusBadge pulse status="pending">
                        Preparing objective
                    </StatusBadge>
                </span>
            );
        }

        if (discoveryStatus === 'running') {
            return (
                <span aria-label={`${pairCounts.running} discovery jobs running out of ${pairCounts.total} total`} role="status">
                    <StatusBadge pulse status="running">
                        Running ({pairCounts.running}/{pairCounts.total})
                    </StatusBadge>
                </span>
            );
        }

        if (discoveryStatus === 'pending') {
            return (
                <StatusBadge status="pending">
                    Queued ({pairCounts.pending}/{pairCounts.total})
                </StatusBadge>
            );
        }

        if (discoveryStatus === 'failed') {
            return (
                <StatusBadge status="failed">
                    Failed ({pairCounts.failed}/{pairCounts.total})
                </StatusBadge>
            );
        }

        if (discoveryStatus === 'completed') {
            return (
                <div className="flex flex-col items-start gap-1">
                    <StatusBadge status="completed">Complete ({pairCounts.completed})</StatusBadge>
                    {catalogAuditPresentation && (
                        <span aria-live="polite" role="status" title={catalogAuditPresentation.title}>
                            <StatusBadge
                                pulse={catalogAuditPresentation.pulse}
                                status={catalogAuditPresentation.badgeStatus}
                                tone={catalogAuditPresentation.badgeStatus === 'running' ? 'warning' : 'success'}
                            >
                                {catalogAuditPresentation.label}
                            </StatusBadge>
                        </span>
                    )}
                </div>
            );
        }

        return (
            <span className="text-zinc-400 text-xs">
                {pairCounts.completed}/{pairCounts.total}
            </span>
        );
    };

    const ResearchWindowBadge: React.FC<{ experiment: ExperimentListItem }> = ({ experiment }) =>
        experiment.trainingEndDateTime && experiment.walkForwardEndDateTime ? (
            <span
                className="text-emerald-400 text-xs"
                title={`Training ends ${experiment.trainingEndDateTime}; sealed walk-forward ends ${experiment.walkForwardEndDateTime}.`}
            >
                Frozen research epoch
            </span>
        ) : (
            <span className="text-amber-400 text-xs" title="This experiment predates immutable research windows.">
                Legacy — rerun required
            </span>
        );

    // Define columns for the DataTable
    const columns: ColumnDef<ExperimentListItem>[] = [
        {
            accessorKey: 'id',
            header: 'ID',
            minSize: 60,
            size: 70,
            maxSize: 80
        },
        {
            accessorKey: 'name',
            header: 'Name',
            minSize: 200,
            size: 300,
            maxSize: 400,
            cell: ({ row }) => (
                <a
                    className="text-blue-400 hover:text-blue-300 underline cursor-pointer"
                    href={`/user/experiment/${row.original.id}`}
                    onClick={(e) => {
                        e.preventDefault();

                        if (row.original.executionStatus === 'completed') {
                            void navigate(`/user/experimentResults/${row.original.id}${preservedStrategyQuery}`);
                        } else {
                            void navigate(`/user/experiment/${row.original.id}${preservedStrategyQuery}`);
                        }
                    }}
                >
                    {row.original.name}
                </a>
            )
        },
        {
            accessorKey: 'strategyName',
            header: 'Strategy',
            minSize: 150,
            size: 200,
            maxSize: 250
        },
        {
            id: 'configuration',
            enableSorting: false,
            header: 'Configuration',
            minSize: 200,
            size: 250,
            maxSize: 300,
            cell: ({ row }) => (
                <div className="text-xs text-zinc-400">
                    {row.original.entityCount} entities × {row.original.durationCount} durations × {row.original.timeHorizonCount} horizons
                </div>
            )
        },
        {
            accessorKey: 'executionStatus',
            header: 'Execution',
            minSize: 100,
            size: 120,
            maxSize: 150,
            cell: ({ row }) => <StatusBadge status={row.original.executionStatus} />
        },
        {
            id: 'discovery',
            enableSorting: false,
            header: 'Discovery',
            minSize: 100,
            size: 140,
            maxSize: 180,
            cell: ({ row }) => <DiscoveryBadge experiment={row.original} />
        },
        {
            id: 'dateRange',
            enableSorting: false,
            header: 'Date Range',
            minSize: 180,
            size: 200,
            maxSize: 250,
            cell: ({ row }) => (
                <div className="text-xs text-zinc-400">
                    <div>
                        {dayjs(row.original.startDateTime).format('MMM D, YYYY')} -{' '}
                        {dayjs(row.original.walkForwardEndDateTime ?? row.original.endDateTime).format('MMM D, YYYY')}
                    </div>
                    <ResearchWindowBadge experiment={row.original} />
                </div>
            )
        },
        {
            accessorKey: 'createdAt',
            header: 'Created',
            minSize: 140,
            size: 160,
            maxSize: 180,
            cell: ({ row }) => <div className="text-xs text-zinc-400">{dayjs(row.original.createdAt).format('MMM D, YYYY h:mm A')}</div>
        },
        {
            id: 'actions',
            enableSorting: false,
            header: 'Actions',
            minSize: 280,
            size: 340,
            maxSize: 420,
            cell: ({ row }) => {
                const experiment = row.original;

                return (
                    <div className="flex flex-wrap gap-2">
                        {experiment.executionStatus === 'draft' && (
                            <button
                                className="px-2 py-1 text-xs text-green-300 hover:text-green-200 bg-green-900/30 hover:bg-green-900/50 rounded transition-colors flex items-center gap-1"
                                title="Start Experiment"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartExperiment(experiment.id);
                                }}
                            >
                                <FaPlay size={10} /> Start
                            </button>
                        )}
                        {experiment.executionStatus === 'failed' && (
                            <button
                                className="px-2 py-1 text-xs text-amber-300 hover:text-amber-200 bg-amber-900/30 hover:bg-amber-900/50 rounded transition-colors flex items-center gap-1"
                                disabled={retryMutation.isPending}
                                title="Retry Failed Work"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedExperiment(experiment);
                                    setShowRetryDialog(true);
                                }}
                            >
                                <FaRedo size={10} /> Retry
                            </button>
                        )}
                        {(experiment.executionStatus === 'running' || experiment.executionStatus === 'queued') && (
                            <button
                                className="px-2 py-1 text-xs text-yellow-300 hover:text-yellow-200 bg-yellow-900/30 hover:bg-yellow-900/50 rounded transition-colors flex items-center gap-1"
                                title="Cancel Experiment"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedExperiment(experiment);
                                    setShowCancelDialog(true);
                                }}
                            >
                                <FaStop size={10} /> Cancel
                            </button>
                        )}
                        {experiment.executionStatus === 'completed' && (
                            <button
                                className="px-2 py-1 text-xs text-blue-300 hover:text-blue-200 bg-blue-900/30 hover:bg-blue-900/50 rounded transition-colors flex items-center gap-1"
                                title="View Results"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigate(`/user/experimentResults/${experiment.id}${preservedStrategyQuery}`);
                                }}
                            >
                                <FaEye size={10} /> Results
                            </button>
                        )}
                        <button
                            className="px-2 py-1 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                void navigate(`/user/experiment/${experiment.id}${preservedStrategyQuery}`);
                            }}
                        >
                            View
                        </button>
                        {experiment.executionStatus !== 'deleting' && (
                            <button
                                className="px-2 py-1 text-xs text-purple-300 hover:text-purple-200 bg-purple-900/30 hover:bg-purple-900/50 rounded transition-colors flex items-center gap-1"
                                disabled={duplicateMutation.isPending}
                                title="Duplicate Settings into New Draft"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    duplicateMutation.mutate(experiment.id);
                                }}
                            >
                                <FaCopy size={10} /> Copy
                            </button>
                        )}
                        {experiment.executionStatus !== 'running' &&
                            experiment.executionStatus !== 'queued' &&
                            experiment.executionStatus !== 'deleting' && (
                                <button
                                    className="px-2 py-1 text-xs text-red-300 hover:text-red-200 bg-red-900/30 hover:bg-red-900/50 rounded transition-colors flex items-center gap-1"
                                    title="Delete Experiment"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedExperiment(experiment);
                                        setShowDeleteDialog(true);
                                    }}
                                >
                                    <FaTrash size={10} />
                                </button>
                            )}
                    </div>
                );
            }
        }
    ];

    // Fetch experiments with tRPC
    const experimentsQuery = useAppQuery(
        {
            ...trpc.experiment.list.queryOptions({
                executionStatus: 'all',
                ...(selectedStrategyId ? { strategyId: selectedStrategyId } : {}),
                query,
                sortBy,
                sortDirection: sorting[0]?.desc === false ? 'ASC' : 'DESC',
                limit: PAGE_SIZE,
                offset: pageIndex * PAGE_SIZE
            }),
            placeholderData: keepPreviousData,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME,
            refetchInterval: (query) => {
                const currentExperiments = query.state.data?.experiments ?? [];

                const hasActiveLifecycle = currentExperiments.some(
                    (experiment) =>
                        experiment.executionStatus === 'queued' ||
                        experiment.executionStatus === 'running' ||
                        experiment.discoveryStatus === 'preparing_objective' ||
                        experiment.discoveryStatus === 'pending' ||
                        experiment.discoveryStatus === 'running' ||
                        experiment.catalogAudit.status === 'pending'
                );

                return hasActiveLifecycle || recentlyStartedExperimentIds.size > 0 ? 3000 : false;
            }
        },
        {
            fallbackErrorMessage: 'Failed to load experiments',
            setError: (msg) => setError(msg)
        }
    );
    useEffect(() => {
        if (!experimentsQuery.data || experimentsQuery.isPlaceholderData || experimentsQuery.isFetching) return;
        const lastPageIndex = Math.max(0, Math.ceil(experimentsQuery.data.total / PAGE_SIZE) - 1);
        if (pageIndex > lastPageIndex) setPageIndex(lastPageIndex);
    }, [experimentsQuery.data, experimentsQuery.isPlaceholderData, experimentsQuery.isFetching, pageIndex, setPageIndex]);

    // Start experiment mutation
    const startMutation = useMutation({
        mutationFn: async (experimentId: number) => {
            await trpcClient.experiment.start.mutate({ experimentId });
        },
        onSuccess: (_, experimentId) => {
            // Add experiment to recently started list
            setRecentlyStartedExperimentIds((prev) => new Set(prev).add(experimentId));

            // Optimistically update the experiment status in the cache
            void experimentsQuery.refetch();

            // Remove from recently started list after 30 seconds
            setTimeout(() => {
                setRecentlyStartedExperimentIds((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(experimentId);
                    return newSet;
                });
            }, 30000);
        },
        onError: (error) => {
            setError(`Failed to start experiment: ${error.message}`);
        }
    });

    // Delete experiment mutation
    const deleteMutation = useMutation({
        mutationFn: async (experimentId: number) => {
            await trpcClient.experiment.delete.mutate({ experimentId });
        },
        onMutate: () => {
            // Show a global spinner while delete is in progress
            showSpinner();
        },
        onSuccess: () => {
            setShowDeleteDialog(false);
            setSelectedExperiment(null);
            void experimentsQuery.refetch();
        },
        onError: (error) => {
            setError(`Failed to delete experiment: ${error.message}`);
        },
        onSettled: () => {
            // Ensure spinner is hidden after delete completes (success or error)
            hideSpinner();
        }
    });

    // Cancel experiment mutation
    const cancelMutation = useMutation({
        mutationFn: async (experimentId: number) => {
            await trpcClient.experiment.cancel.mutate({ experimentId });
        },
        onSuccess: () => {
            setShowCancelDialog(false);
            setSelectedExperiment(null);
            void experimentsQuery.refetch();
        },
        onError: (error) => {
            setError(`Failed to cancel experiment: ${error.message}`);
        }
    });

    const retryMutation = useMutation({
        mutationFn: async (experimentId: number) => {
            return trpcClient.experiment.retryFailed.mutate({ experimentId });
        },
        onSuccess: (result) => {
            setShowRetryDialog(false);
            setSelectedExperiment(null);
            showToast(
                `Retry queued: ${result.requeuedWorkUnits} failed task${result.requeuedWorkUnits === 1 ? '' : 's'}; ${result.completedWorkUnits} completed tasks preserved.`,
                'success'
            );
            void experimentsQuery.refetch();
        },
        onError: (error) => {
            showToast(`Failed to retry experiment: ${error.message}`, 'error');
        }
    });

    const duplicateMutation = useMutation({
        mutationFn: async (experimentId: number) => {
            return trpcClient.experiment.duplicate.mutate({ experimentId });
        },
        onSuccess: (result) => {
            showToast(`Draft experiment #${result.experimentId} created from copied settings.`, 'success');
            void navigate(`/user/experiment/${result.experimentId}${preservedStrategyQuery}`);
        },
        onError: (error) => {
            showToast(`Failed to duplicate experiment: ${error.message}`, 'error');
        }
    });

    // Handle start experiment
    const handleStartExperiment = (experimentId: number) => {
        startMutation.mutate(experimentId);
    };

    // Handle delete confirmation
    const handleDeleteConfirm = () => {
        if (selectedExperiment) {
            deleteMutation.mutate(selectedExperiment.id);
        }
    };

    // Handle cancel confirmation
    const handleCancelConfirm = () => {
        if (selectedExperiment) {
            cancelMutation.mutate(selectedExperiment.id);
        }
    };

    const handleRetryConfirm = () => {
        if (selectedExperiment) {
            retryMutation.mutate(selectedExperiment.id);
        }
    };

    // Extract data from query with optimistic updates for recently started experiments
    const experiments = useMemo((): ExperimentListItem[] => {
        const baseExperiments = experimentsQuery.data?.experiments || [];

        // Apply optimistic updates for recently started experiments
        return baseExperiments.map((exp): ExperimentListItem => {
            if (recentlyStartedExperimentIds.has(exp.id) && exp.executionStatus === 'draft') {
                // Optimistically show as queued until we get the real status
                return {
                    ...exp,
                    executionStatus: 'queued',
                    overallStatus: 'queued',
                    lifecycle: {
                        ...exp.lifecycle,
                        executionStatus: 'queued',
                        overallStatus: 'queued'
                    }
                };
            }

            return exp;
        });
    }, [experimentsQuery.data, recentlyStartedExperimentIds]);

    const { isLoading } = experimentsQuery;

    // Show/hide spinner based on loading state
    useEffect(() => {
        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, [isLoading]);

    // Render loading state
    if (isLoading) {
        return (
            <UserLayout title="Experiments - Project 314">
                <DataLoadingSpinner text="Loading experiments..." />
            </UserLayout>
        );
    }

    // Render error state
    if (error) {
        return (
            <UserLayout title="Experiments - Error - Project 314">
                <div className="container mx-auto">
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
                </div>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1600px" title="Experiments - Project 314">
            <div className="container mx-auto px-4">
                <div className="flex justify-between items-center mt-4 mb-6">
                    <div>
                        <h3 className="text-white text-xl font-semibold mb-2">Strategy Experiments</h3>
                        <p className="text-zinc-400 text-sm">
                            Test trading strategies across multiple entities, timeframes, and horizons to find optimal configurations.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline-dark" onClick={handleCompareExperiments}>
                            Compare Results
                        </Button>
                        <Button variant="primary" onClick={handleCreateExperiment}>
                            Create Experiment
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
                    <div className="min-w-[240px]">
                        <FormInput
                            noMargin
                            label="Search experiments"
                            maxLength={200}
                            placeholder="Name, strategy, description, or ID"
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                        />
                    </div>
                    <div className="min-w-[280px]">
                        <TradingStrategySelect
                            noMargin
                            label="Strategy"
                            placeholder="All strategies"
                            size="sm"
                            value={selectedStrategyId}
                            onChange={setSelectedStrategyId}
                        />
                    </div>
                    {selectedStrategyId && (
                        <Button size="sm" variant="outline-dark" onClick={() => setSelectedStrategyId(null)}>
                            Clear
                        </Button>
                    )}
                    <div className="text-xs text-zinc-500 sm:ml-auto" role="status">
                        {experimentsQuery.isFetching
                            ? 'Updating experiments…'
                            : `Showing ${experiments.length} of ${experimentsQuery.data?.total ?? 0}`}
                    </div>
                </div>

                {/* Summary stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                        <div className="text-zinc-400 text-xs mb-1">Total Experiments</div>
                        <div className="text-white text-2xl font-semibold">{experimentsQuery.data?.total ?? 0}</div>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                        <div className="text-zinc-400 text-xs mb-1">Execution Active · This Page</div>
                        <div className="text-blue-400 text-2xl font-semibold">
                            {experiments.filter((e) => e.executionStatus === 'running' || e.executionStatus === 'queued').length}
                        </div>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                        <div className="text-zinc-400 text-xs mb-1">Discovery/Insights Active · This Page</div>
                        <div className="text-purple-400 text-2xl font-semibold">
                            {
                                experiments.filter(
                                    (e) =>
                                        e.discoveryStatus === 'preparing_objective' ||
                                        e.discoveryStatus === 'pending' ||
                                        e.discoveryStatus === 'running' ||
                                        e.catalogAudit.status === 'pending'
                                ).length
                            }
                        </div>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                        <div className="text-zinc-400 text-xs mb-1">Fully Complete · This Page</div>
                        <div className="text-green-400 text-2xl font-semibold">
                            {experiments.filter((e) => e.overallStatus === 'completed' && e.catalogAudit.status !== 'pending').length}
                        </div>
                    </div>
                </div>

                {/* Display experiments in DataTable */}
                <DataTable
                    manualSorting
                    allowOverflow
                    striped
                    columns={columns}
                    data={experiments}
                    search={false}
                    sorting={sorting}
                    onSortingChange={(next) => {
                        setSorting(next);
                        setPageIndex(0);
                    }}
                    pagination={{
                        enabled: true,
                        pageSize: PAGE_SIZE,
                        server: {
                            pageIndex,
                            rowCount: experimentsQuery.data?.total ?? 0,
                            onPageChange: setPageIndex
                        }
                    }}
                />

                {/* Delete confirmation dialog */}
                <ConfirmationDialog
                    confirmText="Delete"
                    confirmVariant="danger"
                    isOpen={showDeleteDialog}
                    message={`Are you sure you want to delete the experiment "${selectedExperiment?.name}"? This action cannot be undone.`}
                    title="Delete Experiment"
                    onCancel={() => {
                        setShowDeleteDialog(false);
                        setSelectedExperiment(null);
                    }}
                    onConfirm={handleDeleteConfirm}
                />

                {/* Cancel confirmation dialog */}
                <ConfirmationDialog
                    confirmText="Cancel Experiment"
                    confirmVariant="warning"
                    isOpen={showCancelDialog}
                    message={`Are you sure you want to cancel the running experiment "${selectedExperiment?.name}"? This will stop the experiment and mark it as cancelled.`}
                    title="Cancel Experiment"
                    onCancel={() => {
                        setShowCancelDialog(false);
                        setSelectedExperiment(null);
                    }}
                    onConfirm={handleCancelConfirm}
                />

                <ConfirmationDialog
                    confirmText="Retry Failed Work"
                    confirmVariant="warning"
                    isConfirmLoading={retryMutation.isPending}
                    isOpen={showRetryDialog}
                    message={
                        <>
                            Completed work and saved results for <span className="font-semibold">{selectedExperiment?.name}</span> will be
                            preserved. Only terminally failed execution tasks will be requeued.
                        </>
                    }
                    title="Retry Experiment"
                    onCancel={() => {
                        setShowRetryDialog(false);
                        setSelectedExperiment(null);
                    }}
                    onConfirm={handleRetryConfirm}
                />
            </div>
        </UserLayout>
    );
};

export default Experiments;
